import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { feedingsApi } from '../utils/api';
import { handleApiError, formatDate } from '../utils/helpers';
import PhotoModal from '../components/PhotoModal';

const FeederFeedingsPage = () => {
  const location = useLocation();
  const [feedings, setFeedings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentFeedingPhotos, setCurrentFeedingPhotos] = useState([]);
  
  // Check for success message from navigation state
  useEffect(() => {
    if (location.state?.success && location.state?.message) {
      setSuccessMessage(location.state.message);
      
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);
  
  // Fetch feedings
  useEffect(() => {
    const fetchFeedings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await feedingsApi.getFeederFeedings(currentPage, pageSize);
        console.log('Feeder feedings response:', response);
        
        if (Array.isArray(response)) {
          // Direct array response
          setFeedings(response);
          
          // Calculate totalPages based on the array length and pageSize
          // This is a temporary solution until the API returns proper pagination
          setTotalCount(response.length);
          setTotalPages(Math.ceil(response.length / pageSize));
        } else if (response && Array.isArray(response.data)) {
          // Response with data property
          setFeedings(response.data);
          setTotalCount(response.totalCount || response.data.length);
          setTotalPages(response.totalPages || Math.ceil((response.totalCount || response.data.length) / pageSize));
        } else {
          console.error('Unexpected response format:', response);
          setFeedings([]);
          setTotalPages(0);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching feedings:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeedings();
  }, [currentPage, pageSize]);
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Open photo modal
  const openPhotoModal = (feeding, photoIndex) => {
    if (feeding.feedingPhotos && feeding.feedingPhotos.length > 0) {
      // Map the feeding photos to a format expected by the PhotoModal component
      const formattedPhotos = feeding.feedingPhotos.map(photo => ({
        url: photo.path,
        id: photo.id
      }));
      
      setCurrentFeedingPhotos(formattedPhotos);
      setCurrentPhotoIndex(photoIndex);
      setSelectedPhoto(formattedPhotos[photoIndex]);
      setPhotoModalOpen(true);
    }
  };
  
  // Close photo modal
  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedPhoto(null);
  };
  
  // Navigate to next photo
  const nextPhoto = () => {
    if (currentPhotoIndex < currentFeedingPhotos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
      setSelectedPhoto(currentFeedingPhotos[currentPhotoIndex + 1]);
    }
  };
  
  // Navigate to previous photo
  const prevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
      setSelectedPhoto(currentFeedingPhotos[currentPhotoIndex - 1]);
    }
  };
  
  return (
    <div className="feeder-feedings-page">
      <div className="page-header">
        <h1 className="page-title">Feeding History</h1>
        <p className="page-description">
          View all your recorded feedings
        </p>
      </div>
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
          <button 
            className="close-button"
            onClick={() => setSuccessMessage('')}
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {!loading && !error && feedings.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🍽️</div>
          <h2 className="empty-state-title">No feedings recorded yet</h2>
          <p className="empty-state-description">
            You haven't recorded any feedings yet. Feed a cat to get started.
          </p>
          <Link to="/feeder/my-cats" className="button">
            Go to My Cats
          </Link>
        </div>
      )}
      
      {/* Feedings list */}
      {!loading && !error && feedings.length > 0 && (
        <>
          <div className="feeder-feedings-list">
            {feedings.map(feeding => {
              // Extract cat photo from the nested structure - check multiple potential sources
              let catPhoto = null;
              
              // Option 1: cat.catPhotos array
              if (feeding.cat && feeding.cat.catPhotos && feeding.cat.catPhotos.length > 0) {
                catPhoto = feeding.cat.catPhotos[0].path;
                console.log('Using cat.catPhotos[0].path:', catPhoto);
              }
              // Option 2: cat.mainPhoto property
              else if (feeding.cat && feeding.cat.mainPhoto) {
                catPhoto = feeding.cat.mainPhoto;
                console.log('Using cat.mainPhoto:', catPhoto);
              }
              // Option 3: standalone catPhoto property
              else if (feeding.catPhoto) {
                catPhoto = feeding.catPhoto;
                console.log('Using feeding.catPhoto:', catPhoto);
              }
              // Option 4: standalone mainPhoto property
              else if (feeding.mainPhoto) {
                catPhoto = feeding.mainPhoto;
                console.log('Using feeding.mainPhoto:', catPhoto);
              }
              
              console.log('Final cat photo used:', catPhoto || '/cat-placeholder.png');
                
              return (
                <div key={feeding.id} className="feeding-item">
                  <div className="feeding-header">
                    <h3 className="feeding-title">
                      <img 
                        src={catPhoto || '/cat-placeholder.png'} 
                        alt={feeding.cat ? feeding.cat.name : 'Cat'} 
                        className="feeding-cat-image"
                        onError={(e) => {
                          console.log('Image failed to load:', catPhoto);
                          e.target.src = '/cat-placeholder.png';
                        }}
                      />
                      {feeding.cat ? feeding.cat.name : 'Unknown Cat'}
                    </h3>
                    
                    <div className="feeding-meta">
                      <span className="feeding-date">
                        {formatDate(feeding.date, { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  
                  <div className="feeding-details">
                    <div className="feeding-info">
                      {feeding.foodType && feeding.foodAmount && (
                        <p><strong>Food:</strong> {feeding.foodType} ({feeding.foodAmount})</p>
                      )}
                      {feeding.notes && <p className="feeding-notes"><strong>Notes:</strong> {feeding.notes}</p>}
                      {feeding.requestedBy && (
                        <p className="requested-by">
                          <strong>Requested by:</strong> {feeding.requestedBy}
                        </p>
                      )}
                      
                      {/* Completed requests count display */}
                      {feeding.completedRequestsCount !== undefined && (
                        <div className="feeding-stat">
                          <strong>Completed Requests:</strong> 
                          <span className="completed-requests-count">{feeding.completedRequestsCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {feeding.feedingPhotos && feeding.feedingPhotos.length > 0 && (
                    <div className="feeding-photos">
                      {feeding.feedingPhotos.map((photo, index) => (
                        <div 
                          key={index} 
                          className="feeding-photo"
                          onClick={() => openPhotoModal(feeding, index)}
                        >
                          <img src={photo.path} alt={`Feeding photo ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
              >
                First
              </button>
              
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage + 1} of {totalPages} ({totalCount} total)
              </span>
              
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
              >
                Next
              </button>
              
              <button
                className="pagination-button"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
              >
                Last
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Photo Modal */}
      {photoModalOpen && selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          onClose={closePhotoModal}
          onNext={nextPhoto}
          onPrevious={prevPhoto}
          hasNext={currentPhotoIndex < currentFeedingPhotos.length - 1}
          hasPrevious={currentPhotoIndex > 0}
        />
      )}
    </div>
  );
};

export default FeederFeedingsPage; 